/**
 * Date utilities for booking system.
 * Centralizes date formatting, timezone handling, and parsing logic.
 */

import { BOOKING_TIMEZONE, DATE_FORMAT } from './bookingConfig';

/**
 * Get today's date as a string in dd-mm-yyyy format (Singapore timezone).
 * Used for initial selectedDate value and date validation.
 */
export function getCurrentDateString() {
  const date = new Date();
  return date.toLocaleDateString('en-GB', { timeZone: BOOKING_TIMEZONE }).replace(/\//g, '-');
}

/**
 * Format a Date object to dd-mm-yyyy string (Singapore timezone).
 */
export function formatDateToString(date) {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) {
    return '';
  }
  return date.toLocaleDateString('en-GB', { timeZone: BOOKING_TIMEZONE }).replace(/\//g, '-');
}

/**
 * Parse a dd-mm-yyyy date string to a Date object.
 * Returns null if format is invalid.
 */
export function parseDateString(dateStr) {
  if (!dateStr || typeof dateStr !== 'string') return null;
  const parts = dateStr.split('-');
  if (parts.length !== 3) return null;
  const [day, month, year] = parts.map(Number);
  const date = new Date(year, month - 1, day);
  if (Number.isNaN(date.getTime())) return null;
  return date;
}

/**
 * Parse a date/time string from backend (e.g., "29-01-2025 02:30:00 PM").
 * Returns ISO string (YYYY-MM-DDTHH:mm:ss) for further processing, or null.
 */
export function parseBookingDateTimeString(dateStr) {
  if (!dateStr) return null;
  const parts = dateStr.split(' ');
  if (parts.length < 3) return null;

  const [datePart, timePart, meridian] = parts;
  if (!datePart || !timePart || !meridian) return null;

  const [day, month, year] = datePart.split('-');
  let [hour, minute, second] = timePart.split(':');

  const h = parseInt(hour, 10);
  if (meridian === 'PM' && h !== 12) {
    hour = (h + 12).toString();
  } else if (meridian === 'AM' && h === 12) {
    hour = '0';
  }

  const hourStr = parseInt(hour, 10).toString().padStart(2, '0');
  return `${year}-${month}-${day}T${hourStr}:${minute}:${second}`;
}

/**
 * Format a time string (e.g., "14:30:00") to 12-hour format (e.g., "02:30 PM").
 * Useful for display in booking summaries.
 */
export function formatTimeTo12Hour(timeStr) {
  if (!timeStr) return '';
  const [hour, minute] = timeStr.split(':');
  let h = parseInt(hour, 10);
  const meridian = h >= 12 ? 'PM' : 'AM';
  if (h > 12) h -= 12;
  if (h === 0) h = 12;
  return `${h.toString().padStart(2, '0')}:${minute} ${meridian}`;
}

/**
 * Get the end time by adding duration (in minutes) to a start time.
 * Used for service duration calculations.
 */
export function addMinutesToTime(timeStr, minutes) {
  if (!timeStr || !minutes) return timeStr;
  const [hour, minute] = timeStr.split(':').map(Number);
  const totalMinutes = hour * 60 + minute + minutes;
  const newHour = Math.floor(totalMinutes / 60) % 24;
  const newMinute = totalMinutes % 60;
  return `${newHour.toString().padStart(2, '0')}:${newMinute.toString().padStart(2, '0')}:00`;
}

/**
 * Check if a date is in the past (before today in Singapore timezone).
 */
export function isDateInPast(dateStr) {
  const date = parseDateString(dateStr);
  if (!date) return false;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  date.setHours(0, 0, 0, 0);
  return date < today;
}

/**
 * Check if a date is today.
 */
export function isDateToday(dateStr) {
  return dateStr === getCurrentDateString();
}

/** Format the maintenance return time for display in Singapore time. */
export function formatMaintenanceTime(value) {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';

  const day = new Intl.DateTimeFormat('en-GB', {
    timeZone: BOOKING_TIMEZONE,
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  }).format(date);
  const time = new Intl.DateTimeFormat('en-US', {
    timeZone: BOOKING_TIMEZONE,
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  }).format(date);

  return `${day} at ${time} (${BOOKING_TIMEZONE})`;
}
